import dayjs from 'dayjs';
import { ArrowRight, CalendarClock, MapPin } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { cn } from '../../shared/lib/cn';

import { computeStartWindow, type StudentSession } from './api';
import s from './NextSessionCard.module.css';

interface Props {
  session: StudentSession | null;
  // `nowOverride` lets tests freeze the clock without touching globals.
  nowOverride?: Date;
}

function humanizeMs(ms: number): string {
  const totalMinutes = Math.max(0, Math.floor(ms / 60_000));
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m`;
  return 'less than a minute';
}

function formatRange(start: string, end: string): string {
  const opts: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  };
  const startDate = new Date(start);
  const endDate = new Date(end);
  const startStr = startDate.toLocaleString(undefined, opts);
  const endStr = endDate.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${startStr} – ${endStr}`;
}

export function NextSessionCard({ session, nowOverride }: Props): JSX.Element {
  const [now, setNow] = useState<Date>(() => nowOverride ?? new Date());

  // Ticker — wakes once a minute by default, sooner when the window is close.
  useEffect(() => {
    if (nowOverride !== undefined || session === null) return;
    const tick = (): void => setNow(new Date());
    const win = computeStartWindow(new Date(), session);
    const intervalMs = win.state === 'open' || win.msUntilOpen < 60_000 ? 10_000 : 60_000;
    const id = window.setInterval(tick, intervalMs);
    return () => window.clearInterval(id);
  }, [session, nowOverride]);

  if (session === null) {
    return (
      <div className={s.empty}>
        <span className={s.emptyArt} aria-hidden="true">
          <CalendarClock size={24} />
        </span>
        <div>
          <h3 className={s.emptyTitle}>You're all caught up</h3>
          <p className={s.emptyBody}>
            No upcoming sessions. When your institute schedules a mock, it lands here with a
            countdown and a direct join button.
          </p>
        </div>
      </div>
    );
  }

  const win = computeStartWindow(now, session);
  const hasAttempt = session.attempt_id !== null;
  const attemptCompleted =
    session.attempt_status === 'completed' || session.attempt_status === 'grading';

  let statusDotClass = s.statusDot;
  let statusText = '';
  let hint = '';
  if (win.state === 'cancelled') {
    statusDotClass = cn(s.statusDot, s.statusDotCancelled);
    statusText = 'Session cancelled';
    hint = 'Your institute will notify you about a replacement.';
  } else if (attemptCompleted) {
    statusDotClass = cn(s.statusDot, s.statusDotAfter);
    statusText = 'Attempt submitted';
    hint = 'Results land when your tutor finishes grading.';
  } else if (win.state === 'before') {
    statusDotClass = cn(s.statusDot, s.statusDotBefore);
    statusText = `Starts in ${humanizeMs(win.msUntilOpen)}`;
    hint = `Join opens 15 min before ${dayjs(session.scheduled_start).format('h:mm A')}.`;
  } else if (win.state === 'open') {
    statusDotClass = cn(s.statusDot, s.statusDotOpen);
    statusText = hasAttempt ? 'In progress' : 'Window open now';
    hint = `Closes in ${humanizeMs(win.msUntilClose)}.`;
  } else {
    statusDotClass = cn(s.statusDot, s.statusDotAfter);
    statusText = 'Window closed';
    hint = 'The session ended without an attempt being submitted.';
  }

  const canStart =
    win.state === 'open' &&
    !attemptCompleted &&
    (session.status === 'scheduled' || session.status === 'in_progress');

  const startLabel = hasAttempt ? 'Resume exam' : 'Start exam';

  return (
    <div className={s.card}>
      <div className={s.cardInner}>
        <header className={s.head}>
          <span className={s.eyebrow}>Up next</span>
          <span className={s.examChip}>
            {session.exam_type === 'academic' ? 'Academic' : 'General'} IELTS
          </span>
        </header>

        <div>
          <h2 className={s.title}>{session.test_title}</h2>
          <p className={s.subtitle}>at {session.institute_name}</p>
        </div>

        <div className={s.meta}>
          <div className={s.metaItem}>
            <span className={s.metaLabel}>When</span>
            <span className={s.metaValue}>
              {formatRange(session.scheduled_start, session.scheduled_end)}
            </span>
          </div>
          <div className={s.metaItem}>
            <span className={s.metaLabel}>
              <MapPin
                size={11}
                aria-hidden="true"
                style={{ display: 'inline', verticalAlign: '-1px', marginRight: 4 }}
              />
              Location
            </span>
            <span className={s.metaValue}>{session.location ?? '—'}</span>
          </div>
        </div>

        <div className={s.statusRow}>
          <div>
            <div className={s.statusBadge}>
              <span className={statusDotClass} aria-hidden="true" />
              {statusText}
            </div>
            <div className={s.statusHint}>{hint}</div>
          </div>

          <div className={s.actions}>
            {canStart ? (
              <Link
                to={`/student/exam/${session.session_id}`}
                className={s.startBtn}
                aria-label={`${startLabel} for ${session.test_title}`}
              >
                {startLabel}
                <ArrowRight size={14} aria-hidden="true" />
              </Link>
            ) : attemptCompleted ? (
              <Link to="/student/sessions" className={cn(s.startBtn, s.startBtnGhost)}>
                View attempt
                <ArrowRight size={14} aria-hidden="true" />
              </Link>
            ) : (
              <button
                type="button"
                className={s.startBtn}
                disabled
                aria-disabled="true"
                title={
                  win.state === 'cancelled'
                    ? 'Session cancelled'
                    : win.state === 'after'
                      ? 'Session has ended'
                      : 'Available 15 min before start'
                }
              >
                {startLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
